import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { htmlToPlainText } from '@percent/utility'
import { RichTextEditor, type RichTextEditorControl } from '@percent/domain/giving'
import {
  AcknowledgeModal,
  AsyncSelect,
  Box,
  Checkbox,
  FlagAvatar,
  Flex,
  FormField,
  FullPageFormLayout,
  GroupedMultiSelect,
  Loader,
  Modal,
  Radio,
  Select,
  type SelectOption,
  Spacer,
  Text,
  TextInput,
  getInputStatus
} from '@percent/lemonade'
import { ErrorView, WizardHeader } from '@percent/partner-dashboard/common/components'
import { useMutation, useQuery } from '@percent/partner-dashboard/common/hooks'
import { usePointsRewardValue } from '@percent/partner-dashboard/common/hooks/usePointsRewardValue/usePointsRewardValue'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { GivingActionFrequency } from '@percent/partner-dashboard/services/givingActions/givingActionsService.types'
import { useNonprofitLookup } from './useNonprofitLookup'
import { useRegionCountryOptions } from './useRegionCountryOptions'
import styles from './GivingActionForm.module.scss'

const NAME_MAX_CHARS = 120
const DESCRIPTION_MAX_CHARS = 500
const DESCRIPTION_MAX_HTML_CHARS = 5000
const MIN_POINTS = 1
const MAX_POINTS = 10
const DEFAULT_POINTS = 1

const DESCRIPTION_CONTROLS: RichTextEditorControl[] = ['link', 'unlink']

const LIST_ROUTE = '/workplace-giving/campaigns?tab=actions'

const sortedCountries = (codes: readonly string[]) => [...codes].sort().join(',')

type GivingActionFormValues = {
  name: string
  description: string
  targetCountries: string[]
  nonprofitCountryCode: string
  nonprofitId: string
  nonprofitName: string
  rewardsEligible: boolean
  frequency: GivingActionFrequency | ''
  points: number
}

export function GivingActionForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { givingActionsService, nonprofitsService } = useServices()
  const { formatReward } = usePointsRewardValue()
  const { actionId } = useParams<{ actionId: string }>()
  const isEdit = !!actionId

  const [resultState, setResultState] = useState<'success' | 'failure' | undefined>(undefined)

  const [{ data: givingAction, isLoading: isFetching, errorMessage }] = useQuery(
    givingActionsService.getGivingAction,
    actionId ?? '',
    isEdit,
    isEdit
  )

  const [{ isLoading: isCreating }, { apiFunc: createGivingAction }] = useMutation(
    givingActionsService.createGivingAction,
    () => setResultState('success'),
    () => setResultState('failure')
  )

  const [{ isLoading: isUpdating }, { apiFunc: updateGivingAction }] = useMutation(
    givingActionsService.updateGivingAction,
    () => setResultState('success'),
    () => setResultState('failure')
  )

  const { countries } = useCountries()

  const { options: countryGroups, isLoading: isLoadingRegions } = useRegionCountryOptions({
    fallbackGroupLabel: t('typography.givingActions.form.allCountriesGroup')
  })

  const nonprofitCountryOptions = useMemo<SelectOption[]>(
    () =>
      (countries ?? []).map(country => ({
        value: country.code,
        label: country.name,
        prefix: <FlagAvatar code3={country.code} />
      })),
    [countries]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .required(t('errorMessage.required'))
          .max(NAME_MAX_CHARS, t('errorMessage.charactersLimit', { max: NAME_MAX_CHARS })),
        description: Yup.string()
          .required(t('errorMessage.required'))
          .test('description-not-blank', t('errorMessage.required'), value => !!htmlToPlainText(value).trim())
          .test(
            'description-visible-length',
            t('errorMessage.charactersLimit', { max: DESCRIPTION_MAX_CHARS }),
            value => htmlToPlainText(value).length <= DESCRIPTION_MAX_CHARS
          )
          .max(DESCRIPTION_MAX_HTML_CHARS, t('errorMessage.tooMuchFormatting')),
        targetCountries: Yup.array().of(Yup.string().required()).ensure(),
        nonprofitCountryCode: Yup.string().nullable(),
        nonprofitId: Yup.string().nullable(),
        nonprofitName: Yup.string().nullable(),
        rewardsEligible: Yup.boolean().required(),
        frequency: Yup.string()
          .oneOf(['once', 'daily'], t('errorMessage.required'))
          .required(t('errorMessage.required')),
        points: Yup.number()
          .typeError(t('errorMessage.pointsRange', { min: MIN_POINTS, max: MAX_POINTS }))
          .required(t('errorMessage.required'))
          .integer(t('errorMessage.pointsRange', { min: MIN_POINTS, max: MAX_POINTS }))
          .min(MIN_POINTS, t('errorMessage.pointsRange', { min: MIN_POINTS, max: MAX_POINTS }))
          .max(MAX_POINTS, t('errorMessage.pointsRange', { min: MIN_POINTS, max: MAX_POINTS }))
      }),
    [t]
  )

  const { control, handleSubmit, setValue, reset } = useForm<GivingActionFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      description: '',
      targetCountries: [],
      nonprofitCountryCode: '',
      nonprofitId: '',
      nonprofitName: '',
      rewardsEligible: false,
      frequency: '',
      points: DEFAULT_POINTS
    }
  })

  useEffect(() => {
    if (givingAction) {
      reset({
        name: givingAction.name ?? '',
        description: givingAction.description ?? '',
        targetCountries: givingAction.targetCountries ?? [],
        nonprofitCountryCode: '',
        nonprofitId: givingAction.organisationId ?? '',
        nonprofitName: '',
        rewardsEligible: givingAction.rewardsEligible ?? false,
        frequency: givingAction.frequency ?? '',
        points: givingAction.pointValue ?? DEFAULT_POINTS
      })
    }
  }, [givingAction, reset])

  const [{ data: linkedNonprofit }, { query: fetchLinkedNonprofit }] = useQuery(
    nonprofitsService.getNonprofit,
    { id: null },
    false,
    false
  )

  useEffect(() => {
    if (givingAction?.organisationId) {
      fetchLinkedNonprofit({ id: givingAction.organisationId })
    }
  }, [givingAction?.organisationId, fetchLinkedNonprofit])

  useEffect(() => {
    if (linkedNonprofit) {
      setValue('nonprofitName', linkedNonprofit.name)
    }
  }, [linkedNonprofit, setValue])

  const watchedValues = useWatch({ control })
  const watchedNonprofitCountryCode = watchedValues.nonprofitCountryCode ?? ''
  const watchedPoints = watchedValues.points
  const pointsRewardValue =
    typeof watchedPoints === 'number' && watchedPoints >= MIN_POINTS && watchedPoints <= MAX_POINTS
      ? formatReward(watchedPoints, !!watchedValues.rewardsEligible)
      : undefined

  const {
    searchQuery,
    setSearchQuery,
    options: nonprofitOptions,
    isLoading: isNonprofitLoading
  } = useNonprofitLookup(watchedNonprofitCountryCode)

  const submit = handleSubmit(values => {
    const payload = {
      name: values.name.trim(),
      description: values.description,
      frequency: values.frequency as GivingActionFrequency,
      pointValue: values.points,
      targetCountries: values.targetCountries.length ? values.targetCountries : null,
      organisationId: values.nonprofitId || null,
      rewardsEligible: values.rewardsEligible
    }

    if (isEdit && actionId) {
      updateGivingAction({ actionId, ...payload })

      return
    }

    createGivingAction(payload)
  })

  const handleNonprofitCountryChange = (option: SelectOption) => {
    setValue('nonprofitCountryCode', option.value, { shouldValidate: true })
    setValue('nonprofitId', '', { shouldValidate: true })
    setValue('nonprofitName', '', { shouldValidate: true })
    setSearchQuery('')
  }

  const handleNonprofitChange = (option: SelectOption | null) => {
    setValue('nonprofitId', option?.value ?? '', { shouldValidate: true })
    setValue('nonprofitName', option?.label ?? '', { shouldValidate: true })
  }

  const nameValue = watchedValues.name ?? ''
  const descriptionValue = watchedValues.description ?? ''
  const targetCountriesValue = watchedValues.targetCountries ?? []

  const descriptionText = useMemo(() => htmlToPlainText(descriptionValue), [descriptionValue])

  const isSaving = isCreating || isUpdating
  const isEmpty = !nameValue.trim() || !descriptionText.trim() || !watchedValues.frequency || !watchedValues.points

  const isUnchanged =
    isEdit &&
    nameValue.trim() === (givingAction?.name?.trim() ?? '') &&
    descriptionValue === (givingAction?.description ?? '') &&
    sortedCountries(targetCountriesValue) === sortedCountries(givingAction?.targetCountries ?? []) &&
    (watchedValues.nonprofitId || '') === (givingAction?.organisationId ?? '') &&
    watchedValues.frequency === givingAction?.frequency &&
    watchedValues.points === givingAction?.pointValue &&
    !!watchedValues.rewardsEligible === !!givingAction?.rewardsEligible

  // The region config decides whether the country picker is grouped, so it must resolve before the
  // picker renders.
  const showLoader = (isEdit && isFetching) || isLoadingRegions
  const showError = isEdit && !isFetching && (!!errorMessage || !givingAction)
  const isSubmitDisabled = isFetching || isEmpty || isUnchanged || showError

  const handleBack = () => navigate(LIST_ROUTE)
  const handleFailureClose = () => setResultState(undefined)

  return (
    <Box className={styles.pageWrapper} data-testid="giving-action-form">
      <FullPageFormLayout
        actionsHeader={
          <WizardHeader
            title={
              isEdit ? t('typography.givingActions.form.editTitle') : t('typography.givingActions.form.createTitle')
            }
            onCancel={handleBack}
            onPublish={submit}
            isPublishDisabled={isSubmitDisabled}
            isLoading={isSaving}
            publishBtnCopy={isEdit ? t('button.updateGivingAction') : t('button.createGivingAction')}
            toolTipCopy={isSubmitDisabled ? t('toolTip.requiredFields') : ''}
            publishBtnTestId="giving-action-submit-button"
            cancelBtnTestId="giving-action-cancel-button"
          />
        }
      >
        {showLoader && <Loader />}
        {showError && <ErrorView errorMessage={errorMessage} />}
        {!showLoader && !showError && (
          <Flex direction="column" className={styles.formWrapper}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => {
                const fieldError = fieldState.isTouched ? fieldState.error?.message : undefined

                return (
                  <FormField
                    label={t('typography.givingActions.form.nameLabel')}
                    necessity="required"
                    status={getInputStatus(fieldError)}
                    statusMessage={fieldError}
                    description={t('typography.givingActions.form.charactersLeft', {
                      count: Math.max(NAME_MAX_CHARS - (field.value?.length ?? 0), 0)
                    })}
                  >
                    <TextInput
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('typography.givingActions.form.namePlaceholder')}
                      maxLength={NAME_MAX_CHARS}
                      data-testid="giving-action-name-input"
                    />
                  </FormField>
                )
              }}
            />

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="description"
              render={({ field, fieldState }) => {
                const fieldError = fieldState.isTouched ? fieldState.error?.message : undefined

                return (
                  <FormField
                    label={t('typography.givingActions.form.descriptionLabel')}
                    necessity="required"
                    status={getInputStatus(fieldError)}
                    statusMessage={fieldError}
                    description={t('typography.givingActions.form.descriptionHint', {
                      count: Math.max(DESCRIPTION_MAX_CHARS - descriptionText.length, 0)
                    })}
                  >
                    <RichTextEditor
                      fieldName={field.name}
                      content={field.value}
                      handleUpdate={field.onChange}
                      handleBlur={field.onBlur}
                      error={!!fieldError}
                      controls={DESCRIPTION_CONTROLS}
                      placeholder={t('typography.givingActions.form.descriptionPlaceholder')}
                      testId="giving-action-description-input"
                    />
                  </FormField>
                )
              }}
            />

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="nonprofitCountryCode"
              render={({ field }) => (
                <FormField label={t('typography.givingActions.form.nonprofitCountryLabel')} necessity="optional">
                  <Select
                    key={field.value || 'nonprofit-country-empty'}
                    placeholder={t('typography.givingActions.form.nonprofitCountryPlaceholder')}
                    options={nonprofitCountryOptions}
                    defaultValue={nonprofitCountryOptions.find(option => option.value === field.value)}
                    onChange={handleNonprofitCountryChange}
                    searchable
                    showPrefixForSelectedOption
                    data-testid="giving-action-nonprofit-country-select"
                  />
                </FormField>
              )}
            />

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="nonprofitId"
              render={({ field }) => (
                <FormField label={t('typography.givingActions.form.nonprofitLabel')} necessity="optional">
                  <AsyncSelect
                    key={watchedNonprofitCountryCode}
                    placeholder={t('typography.givingActions.form.nonprofitPlaceholder')}
                    options={nonprofitOptions}
                    query={searchQuery}
                    setQuery={setSearchQuery}
                    loading={searchQuery.length > 0 && isNonprofitLoading && !field.value}
                    loadingText={t('typography.searching')}
                    noResultsFoundText={t(
                      searchQuery ? 'typography.noResultsFound' : 'typography.pleaseEnterSearchTerm'
                    )}
                    minSearchLength={3}
                    minSearchLengthMessage={t('typography.minSearchLengthMessage', { count: 3 })}
                    defaultValue={
                      field.value && watchedValues.nonprofitName
                        ? { value: field.value, label: watchedValues.nonprofitName }
                        : undefined
                    }
                    onChange={handleNonprofitChange}
                    clearable
                    data-testid="giving-action-nonprofit-select"
                  />
                </FormField>
              )}
            />

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="targetCountries"
              render={({ field }) => (
                <FormField
                  label={t('typography.givingActions.form.countryLabel')}
                  necessity="optional"
                  description={t('typography.givingActions.form.countryDescription')}
                >
                  <GroupedMultiSelect
                    data={countryGroups}
                    value={field.value}
                    onChange={field.onChange}
                    triggerPlaceholder={t('typography.givingActions.form.countryPlaceholder')}
                    searchPlaceholder={t('typography.givingActions.form.countrySearchPlaceholder')}
                    selectAllLabel={t('typography.givingActions.form.countrySelectAll')}
                    getFooterTranslation={count => t('typography.givingActions.form.countriesSelected', { count })}
                    data-testid="giving-action-country-select"
                  />
                </FormField>
              )}
            />

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="frequency"
              render={({ field, fieldState }) => (
                <FormField
                  label={t('typography.givingActions.form.frequencyLabel')}
                  necessity="required"
                  status={getInputStatus(fieldState.error?.message)}
                  statusMessage={fieldState.error?.message}
                  description={t('typography.givingActions.form.frequencyDescription')}
                >
                  <Flex direction="row" gap={24}>
                    <Radio
                      id="frequency-once"
                      name={field.name}
                      value="once"
                      label={t('typography.givingActions.frequency.once')}
                      checked={field.value === 'once'}
                      onChange={() => field.onChange('once')}
                      data-testid="giving-action-frequency-once"
                    />
                    <Radio
                      id="frequency-daily"
                      name={field.name}
                      value="daily"
                      label={t('typography.givingActions.frequency.daily')}
                      checked={field.value === 'daily'}
                      onChange={() => field.onChange('daily')}
                      data-testid="giving-action-frequency-daily"
                    />
                  </Flex>
                </FormField>
              )}
            />

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="points"
              render={({ field, fieldState }) => {
                const fieldError = fieldState.isTouched ? fieldState.error?.message : undefined

                return (
                  <FormField
                    label={t('typography.givingActions.form.pointsLabel')}
                    necessity="required"
                    status={getInputStatus(fieldError)}
                    statusMessage={fieldError}
                    description={t('typography.givingActions.form.pointsDescription', {
                      min: MIN_POINTS,
                      max: MAX_POINTS
                    })}
                  >
                    <TextInput
                      name={field.name}
                      type="number"
                      min={MIN_POINTS}
                      max={MAX_POINTS}
                      value={field.value === undefined || Number.isNaN(field.value) ? '' : field.value}
                      onChange={event => field.onChange(event.target.valueAsNumber)}
                      onBlur={field.onBlur}
                      data-testid="giving-action-points-input"
                    />
                  </FormField>
                )
              }}
            />

            {pointsRewardValue ? (
              <Text size="small" dataTestId="giving-action-points-reward-helper">
                {t('typography.givingActions.form.pointsRewardHelper', { value: pointsRewardValue })}
              </Text>
            ) : null}

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="rewardsEligible"
              render={({ field }) => (
                <FormField label={t('typography.givingActions.form.rewardsLabel')} necessity="optional">
                  <Checkbox
                    name={field.name}
                    variant="default"
                    label={t('typography.givingActions.form.rewardsToggleLabel')}
                    helperText={t('typography.givingActions.form.rewardsDescription')}
                    active={!!field.value}
                    onChange={event => field.onChange(event.currentTarget.checked)}
                    dataTestId="giving-action-rewards-checkbox"
                  />
                </FormField>
              )}
            />
          </Flex>
        )}
      </FullPageFormLayout>

      <Modal
        open={!!resultState}
        onClose={resultState === 'success' ? handleBack : handleFailureClose}
        data-testid="giving-action-result-modal"
      >
        {resultState === 'success' && (
          <AcknowledgeModal
            result="positive"
            title={
              isEdit
                ? t('typography.givingActions.form.editSuccessTitle')
                : t('typography.givingActions.form.createSuccessTitle')
            }
            description={t('typography.givingActions.form.saveSuccessDescription')}
            buttonText={t('button.close')}
            handleClose={handleBack}
            viewTestId="giving-action-success"
            buttonTestId="giving-action-success-close"
          />
        )}
        {resultState === 'failure' && (
          <AcknowledgeModal
            result="negative"
            title={
              isEdit
                ? t('typography.givingActions.form.editErrorTitle')
                : t('typography.givingActions.form.createErrorTitle')
            }
            description={t('typography.givingActions.form.saveErrorDescription')}
            buttonText={t('button.close')}
            handleClose={handleFailureClose}
            viewTestId="giving-action-error"
            buttonTestId="giving-action-error-close"
          />
        )}
      </Modal>
    </Box>
  )
}
